import { IActionCategory, IComponentType, IPresetAnimation, ITrackingTypes, ROOT_COMPNENT_ID, } from '@r3f-component-data-structure';
import { isAbstractComponent } from '@r3f/utils/general';
import { getScreenContentIdForScene } from '@r3f/utils/pure';
import * as Sentry from '@sentry/react';
export const isTrackingImageUsed = (componentsById, trackingImageId) => {
    return (Object.values(componentsById).filter((component) => {
        if (component.type === IComponentType.Scene && component.trackingType === ITrackingTypes.image && component.trackingImageId === trackingImageId)
            return true;
    }).length > 0);
};
/**
 * Use this to finalise the content doc before preview / publish so Jute gets just what it needs
 */
export const finaliseContentDoc = (contentDoc) => {
    let contentDocCopy = JSON.parse(JSON.stringify(contentDoc));
    contentDocCopy = removeRedundantTracking((removeUnlinkedScenes(removeAllAnimationNamesFromModel3ds(removeOrphanedChildIds(removeOrphanedComponents(contentDocCopy))))));
    return removeInvalidActions(contentDocCopy);
};
const getUnUsedImageTrackerIds = (contentDoc) => {
    const componentsById = contentDoc.componentsById;
    const usedImageTrackerIds = [];
    for (const id in componentsById) {
        const component = componentsById[id];
        if (component.type === IComponentType.Scene && component.trackingType === ITrackingTypes.image) {
            const { trackingImageId } = component;
            if (!usedImageTrackerIds.includes(trackingImageId))
                usedImageTrackerIds.push(trackingImageId);
        }
    }
    const unusedImageTrackerIds = Object.keys(contentDoc.tracking?.image ?? {}).filter((trackerId) => !usedImageTrackerIds.includes(trackerId));
    return unusedImageTrackerIds;
};
// /**
//  * Deletes all entries from mediaFilesById that are not referenced
//  * by any filestoreId in componentsById
//  *
//  * @param {Object} contentDoc - The parsed content object containing componentsById and mediaFilesById
//  * @returns {IContentDoc} Adjusted content doc
//  */
// export const removeUnreferencedMediaFileReferences = (contentDoc: IContentDoc): IContentDoc => {
// 	const { componentsById, mediaFilesById } = contentDoc;
// 	if (!componentsById || !mediaFilesById) {
// 		return contentDoc;
// 	}
// 	// Collect all filestoreIds referenced in componentsById
// 	const referencedFilestoreIds = new Set();
// 	for (const componentId in componentsById) {
// 		const component = componentsById[componentId] as IImage | IModel3d;
// 		// Check if component has a filestoreId property
// 		if (component.filestoreId) {
// 			referencedFilestoreIds.add(component.filestoreId);
// 		}
// 	}
// 	// Find and delete unreferenced media file references
// 	for (const key in mediaFilesById) {
// 		const mediaId = mediaFilesById[key].id;
// 		if (!referencedFilestoreIds.has(mediaId)) {
// 			delete mediaFilesById[key];
// 		}
// 	}
// 	return contentDoc;
// }
/**
 * This function removes the .tracking property of the content doc if there are no image tracked scenes present
 */
export const removeRedundantTracking = (contentDocCopy) => {
    // Are there any image tracked scenes?
    const imageTrackedSceneCount = Object.entries(contentDocCopy.componentsById).reduce((imageTrackSceneCount, [_key, value]) => {
        if (value.type === IComponentType.Scene && value.trackingType === ITrackingTypes.image)
            imageTrackSceneCount++;
        return imageTrackSceneCount;
    }, 0);
    // If so, return early ( this func just removes redundant data )
    if (imageTrackedSceneCount) {
        const unusedImageTrackerIds = getUnUsedImageTrackerIds(contentDocCopy);
        for (const trackerId of unusedImageTrackerIds) {
            if (contentDocCopy.tracking?.image?.[trackerId])
                delete contentDocCopy.tracking.image[trackerId];
        }
        return contentDocCopy;
    }
    // Else check for image tracked doc section and remove if applicable
    if (contentDocCopy.tracking) {
        delete contentDocCopy.tracking;
    }
    return contentDocCopy;
};
/**
 * Remove any hidden spatial entities, and all references to them in AR and SR layers
 */
export const removeHiddenEntities = (contentDoc) => {
    const contentDocCopy = JSON.parse(JSON.stringify(contentDoc));
    contentDocCopy.componentsById = Object.entries(contentDocCopy.componentsById).reduce((componentsById, [entityId, entity]) => {
        if (isAbstractComponent(entity)) {
            componentsById[entityId] = entity;
        }
        else {
            if (!entity?.isHidden) {
                componentsById[entityId] = entity;
            }
        }
        // Remove hidden entities from list of children of scene
        if (entity.type === IComponentType.Scene || entity.type === IComponentType.ScreenAnchorGroup || entity.type === IComponentType.FaceLandmarkGroup) {
            entity.children = entity.children.filter((entityId) => {
                return !contentDocCopy.componentsById[entityId].isHidden;
            });
        }
        // Remove any actions that reference a hidden entitiy
        if (!isAbstractComponent(entity)) {
            if (entity.actions) {
                const actionsDict = entity.actions || {};
                Object.entries(actionsDict).forEach(([triggerType, actionArray]) => {
                    // Remove any actions whose target ID is a hidden component
                    actionsDict[triggerType] = actionArray.filter((action) => {
                        if (action.type == IActionCategory.animateModel || action.type == IActionCategory.playVideo) {
                            const targetId = action.targetIds[0]; // Only 1 per action
                            if (contentDocCopy.componentsById[targetId].isHidden) {
                                return false;
                            }
                        }
                        return true;
                    });
                });
                // It's possible we've removed ALL actions for a given trigger, if so remove that key from the actionsDict
                Object.keys(actionsDict).forEach((trigger) => {
                    if (actionsDict[trigger]?.length == 0)
                        delete actionsDict[trigger];
                });
                // It's also possible we've removed ALL actions for ALL trigggers, if so remove the actions property from the entity
                if (Object.keys(actionsDict).length == 0)
                    delete entity.actions;
            }
        }
        return componentsById;
    }, {});
    return contentDocCopy;
};
const deleteUnlinkedScenesFromContentDoc = (sceneIds, contentDocCopy, exludedsceneIds) => {
    sceneIds
        .filter((id) => !exludedsceneIds?.includes(id))
        .forEach((sceneId) => {
        if (!isSceneReferencedFromAnother(contentDocCopy.componentsById, sceneId, contentDocCopy.tracking?.image) && !isFirstScene(contentDocCopy, sceneId)) {
            // console.log(`Scene ${sceneId} is not referenced from any other`);
            let entitiesToRemove = getUnreferencedChildren(sceneId, contentDocCopy.componentsById);
            const screenContentComponentId = getScreenContentIdForScene(contentDocCopy.componentsById[sceneId], contentDocCopy.componentsById);
            if (screenContentComponentId) {
                const anchorGroups = contentDocCopy.componentsById[screenContentComponentId].children;
                entitiesToRemove = [...entitiesToRemove, ...anchorGroups, screenContentComponentId];
                anchorGroups.forEach((anchorGroupId) => {
                    // ensure that duplicated screen ui entities used in over scenes are not removed from content doc
                    const srEntities = getUnreferencedChildren(anchorGroupId, contentDocCopy.componentsById);
                    entitiesToRemove = [...entitiesToRemove, ...srEntities];
                });
            }
            // console.log(entitiesToRemove);
            entitiesToRemove.forEach((entityId) => {
                delete contentDocCopy.componentsById[entityId];
            });
            delete contentDocCopy.componentsById[sceneId];
            contentDocCopy.componentsById[contentDocCopy.rootComponentId].children = contentDocCopy.componentsById[contentDocCopy.rootComponentId].children.filter((id) => id !== sceneId);
        }
    });
    return contentDocCopy;
};
const getUnreferencedChildren = (parentId, componentsById) => {
    const parent = componentsById[parentId];
    if (!isAbstractComponent(parent))
        return [];
    const sceneChildrenSet = new Set(parent.children);
    const referencedChildren = new Set();
    for (const [id, component] of Object.entries(componentsById)) {
        if (id === parentId || !isAbstractComponent(component))
            continue;
        for (const childId of component.children) {
            if (sceneChildrenSet.has(childId)) {
                referencedChildren.add(childId);
            }
        }
    }
    const unreferencedChildren = parent.children.filter((id) => !referencedChildren.has(id));
    return unreferencedChildren;
};
const contentDocHasMultiImageScanAction = (contentDoc) => {
    let hasMultiImageScanAction = false;
    const componentsById = contentDoc.componentsById;
    for (const comp of Object.values(componentsById)) {
        const component = comp;
        if (!!component.actions && hasMultiScanAction(component))
            hasMultiImageScanAction = true;
    }
    return hasMultiImageScanAction;
};
/**
 * Return true if entity has a multi scan action
 */
const hasMultiScanAction = (entity) => {
    const actions = entity.actions;
    if (!actions)
        return false;
    return Object.entries(actions).reduce((hasMultiImageScanAction, [_trigger, actionData]) => {
        if (actionData.reduce((hasAction, action) => {
            if (action.type === IActionCategory.multiImageScan)
                hasAction = true;
            return hasAction;
        }, false)) {
            hasMultiImageScanAction = true;
        }
        return hasMultiImageScanAction;
    }, false);
};
/**
 * Removes any scenes that cannot be reached, including all SR and AR spatial and abstract components
 */
export const removeUnlinkedScenes = (contentDocCopy) => {
    let sceneIdsExcludedFromRemoval;
    const rootComponent = contentDocCopy.componentsById[contentDocCopy.rootComponentId];
    const firstScene = contentDocCopy.componentsById[rootComponent.children[0]];
    const isMultiImageTrackedProject = Object.keys(contentDocCopy.tracking?.image ?? {}).length > 1;
    if (isMultiImageTrackedProject) {
        const _contentDocCopy = JSON.parse(JSON.stringify(contentDocCopy));
        const contentDocWithOnlyActionLinkedScenes = deleteUnlinkedScenesFromContentDoc(getScenes(_contentDocCopy, true), _contentDocCopy);
        // console.log('only linked components', contentDocWithOnlyActionLinkedScenes.componentsById)
        const isOneOrMoreImgTrackedSceneLinked = getScenes(contentDocWithOnlyActionLinkedScenes, true).some((sceneId) => {
            const scene = contentDocWithOnlyActionLinkedScenes.componentsById[sceneId];
            // if (scene.trackingType === ITrackingTypes.image) console.log('linked image tracked scene', scene)
            return scene.trackingType === ITrackingTypes.image;
        });
        // console.log({isOneOrMoreImgTrackedSceneLinked});
        const firstSceneIsImageTracked = firstScene.trackingType === ITrackingTypes.image;
        const haveRemainingScenesMultiScanAction = contentDocHasMultiImageScanAction(contentDocWithOnlyActionLinkedScenes);
        const areAllImageTrackedScenesImageScanLinked = isOneOrMoreImgTrackedSceneLinked || haveRemainingScenesMultiScanAction || firstSceneIsImageTracked;
        if (areAllImageTrackedScenesImageScanLinked) {
            const imageTrackedSceneIds = getScenes(contentDocCopy, true).filter((sceneId) => {
                const scene = contentDocCopy.componentsById[sceneId];
                return scene.trackingType === ITrackingTypes.image;
            });
            sceneIdsExcludedFromRemoval = imageTrackedSceneIds;
        }
    }
    const prunedContentDoc = deleteUnlinkedScenesFromContentDoc(getScenes(contentDocCopy, true), contentDocCopy, sceneIdsExcludedFromRemoval);
    // const rootId = contentDocCopy.rootComponentId;
    // const sceneIds = getScenes(contentDocCopy, true);
    // sceneIds.forEach((sceneId) => {
    // 	if (!isSceneReferencedFromAnother(contentDocCopy.componentsById, sceneId, contentDocCopy.tracking?.image) && !isFirstScene(contentDocCopy, sceneId)) {
    // 		// console.log(`Scene ${sceneId} is not referenced from any other`);
    // 		let entitiesToRemove = (contentDocCopy.componentsById[sceneId] as ISceneComp).children;
    // 		const screenContentComponentId = getScreenContentIdForScene(contentDocCopy.componentsById[sceneId] as ISceneComp, contentDocCopy.componentsById);
    // 		if (screenContentComponentId) {
    // 			const anchorGroups = (contentDocCopy.componentsById[screenContentComponentId] as IScreenContent).children;
    // 			entitiesToRemove = [...entitiesToRemove, ...anchorGroups, screenContentComponentId];
    // 			anchorGroups.forEach((anchorGroupId) => {
    // 				const srEntities = (contentDocCopy.componentsById[anchorGroupId] as IScreenAnchorGroup).children;
    // 				entitiesToRemove = [...entitiesToRemove, ...srEntities];
    // 			});
    // 		}
    // 		// console.log(entitiesToRemove);
    // 		entitiesToRemove.forEach((entityId) => {
    // 			delete contentDocCopy.componentsById[entityId];
    // 		});
    // 		delete contentDocCopy.componentsById[sceneId];
    // 		(contentDocCopy.componentsById[rootId] as IRoot).children = (contentDocCopy.componentsById[rootId] as IRoot).children.filter((id) => id !== sceneId);
    // 	}
    // });
    return prunedContentDoc;
};
const isFirstScene = (contentDoc, sceneId) => {
    const rootId = contentDoc.rootComponentId;
    return contentDoc.componentsById[rootId].children[0] == sceneId;
};
function getScenes(contentDoc, onlyIds = false) {
    return Object.entries(contentDoc.componentsById).reduce((scenes, [entityId, entity]) => {
        if (entity.type == IComponentType.Scene && onlyIds)
            scenes.push(entityId);
        if (entity.type == IComponentType.Scene && !onlyIds)
            scenes.push(entity);
        return scenes;
    }, []);
}
/**
 * Return true if sceneId is referenced from another scene ( false if it is unlinked )
 */
const isSceneReferencedFromAnother = (componentsById, sceneId, imageTrackingDict) => {
    // If the scene is image tracked and multi
    if (componentsById[sceneId].trackingType === ITrackingTypes.image && Object.keys(imageTrackingDict || {}).length > 1)
        return true;
    // Only way to link to a scene is via a linkScene action
    return Object.entries(componentsById).reduce((isReferenced, [_componentId, component]) => {
        if (isAbstractComponent(component) && component.type !== IComponentType.Scene)
            return isReferenced;
        if (hasLinkSceneActionReference(component, sceneId, componentsById))
            isReferenced = true;
        if (isTrackingImageStartScene(sceneId, imageTrackingDict))
            isReferenced = true;
        return isReferenced;
    }, false);
};
/**
 * Return true if entity has a linkScene action which references sceneId
 */
const hasLinkSceneActionReference = (entity, sceneId, componentsById) => {
    const actions = entity.actions;
    if (!actions)
        return false;
    return Object.entries(actions).reduce((hasLinkSceneAction, [_trigger, actionData]) => {
        if (actionData.reduce((isLinkScene, action) => {
            if ((action.type == IActionCategory.linkScene && action.sceneId == sceneId) ||
                (action.type === IActionCategory.multiImageScan && (componentsById?.[sceneId]).trackingType === ITrackingTypes.image))
                isLinkScene = true;
            return isLinkScene;
        }, false)) {
            hasLinkSceneAction = true;
        }
        return hasLinkSceneAction;
    }, false);
};
/**
 * Return true if the scene is a start scene for a tracking image
 */
export const isTrackingImageStartScene = (sceneId, imageTrackingDict) => {
    let isReferenced = false;
    if (!imageTrackingDict)
        return isReferenced;
    for (const trackingImg of Object.values(imageTrackingDict)) {
        if (trackingImg.startSceneId === sceneId)
            isReferenced = true;
    }
    return isReferenced;
};
export const getAnchorGroupIdAtPosition = (position, componentsById, screenContentId) => {
    const anchorGroupIds = componentsById[screenContentId].children;
    for (let i = 0; i < anchorGroupIds.length; i++) {
        const anchorGroup = componentsById[anchorGroupIds[i]];
        if (anchorGroup.anchorPositionType == position) {
            return anchorGroupIds[i];
        }
    }
    return '';
};
/**
 * Remove orphaned child ids from abstract components if no state assigned in componentsById
 */
export const removeOrphanedChildIds = (contentDocCopy) => {
    const componentIdsWithStateAssigned = Object.keys(contentDocCopy.componentsById);
    const orphanedChildIds = [];
    for (const component of Object.values(contentDocCopy.componentsById)) {
        // skip if it is a spatial component which have no children
        if (!isAbstractComponent(component))
            continue;
        // remove child ids with missing state
        component.children = component.children.filter((childId) => {
            const childIdHasComponentAssigned = componentIdsWithStateAssigned.includes(childId);
            if (!childIdHasComponentAssigned)
                orphanedChildIds.push(childId);
            return childIdHasComponentAssigned;
        });
    }
    // Send info to sentry if orphaned child ids found
    if (orphanedChildIds.length > 0) {
        Sentry.captureException('Orphaned child id(s) found without component in content doc', {
            extra: { componentsById: JSON.stringify(contentDocCopy.componentsById), orphanedChildIds },
        });
    }
    return contentDocCopy;
};
/**
 * Remove orphaned component from componentsById (except root) if not an abstract component child
 */
export const removeOrphanedComponents = (contentDocCopy) => {
    const componentIdsWithStateAssigned = Object.keys(contentDocCopy.componentsById);
    const childIds = getChildIds(contentDocCopy);
    const orphanedComponentIds = [];
    for (const componentId of componentIdsWithStateAssigned) {
        // skip if root component as its not a child of any other component
        if (componentId === ROOT_COMPNENT_ID)
            continue;
        // delete component if not a child of an abstract component (i.e. orphaned state)
        if (childIds.includes(componentId))
            continue;
        orphanedComponentIds.push(componentId);
        delete contentDocCopy.componentsById[componentId];
    }
    // Send info to sentry if orphaned component ids found
    if (orphanedComponentIds.length > 0) {
        Sentry.captureException('Orphaned component found without parent in content doc', {
            extra: { componentsById: JSON.stringify(contentDocCopy.componentsById), orphanedComponentIds },
        });
    }
    return contentDocCopy;
};
const getChildIds = (contentDoc) => {
    const childIds = [];
    for (const component of Object.values(contentDoc.componentsById)) {
        // skip if it is a spatial component which have no children
        if (!isAbstractComponent(component))
            continue;
        childIds.push(...component.children);
    }
    return childIds;
};
/**
 * Remove allAnimationNames property from Model3d entities
 */
export const removeAllAnimationNamesFromModel3ds = (contentDocCopy) => {
    for (const component of Object.values(contentDocCopy.componentsById)) {
        if (component.type !== IComponentType.Model3d)
            continue;
        if (component.animations)
            delete component.animations;
    }
    return contentDocCopy;
};
export const getComponentParentIdById = (id, componentsById) => {
    const [parentId = null] = Object.keys(componentsById).filter((_id) => {
        const component = componentsById[_id];
        return isAbstractComponent(component) && component.children.includes(id);
    });
    return parentId;
};
export const getChildlessAbstractComponentIds = (componentsById) => {
    return Object.keys(componentsById).filter((id) => {
        const component = componentsById[id];
        return isAbstractComponent(component) && component.type !== IComponentType.ScreenContent && component.children.length === 0;
    });
};
export const getOrphanComponentIds = (componentsById) => {
    const orphanIds = [];
    const allChildren = [];
    // Get all children
    Object.entries(componentsById).forEach(([_componentId, component]) => {
        if (isAbstractComponent(component)) {
            allChildren.push(...component.children); // This includes abstract components but thats ok ( don't want to remove them if they're referenced )
        }
    });
    // Get any orphan IDs
    Object.entries(componentsById).forEach(([componentId, _component]) => {
        if (!isAbstractComponent) {
            if (!allChildren.includes(componentId)) {
                orphanIds.push(componentId);
            }
        }
    });
    return orphanIds;
};
export const getActionDataWithReferencingPivots = (deletedComponentIds, componentsById) => {
    const referencingActionsData = [];
    for (let i = 0; i < deletedComponentIds.length; i++) {
        const deletedId = deletedComponentIds[i];
        for (const id in componentsById) {
            const component = componentsById[id];
            if (component.actions) {
                const actions = component.actions;
                for (const trigger in actions) {
                    const actionArray = actions[trigger];
                    if (!actionArray)
                        continue;
                    for (let j = 0; j < actionArray.length; j++) {
                        const action = actionArray[j];
                        if (action.type === IActionCategory.animatePreset) {
                            if (action.entityAnimation === IPresetAnimation.orbit) {
                                if (action.pivot === deletedId)
                                    referencingActionsData.push({
                                        entityId: id,
                                        triggerType: trigger,
                                        index: j,
                                    });
                            }
                        }
                    }
                }
            }
        }
    }
    return referencingActionsData;
};
function removeInvalidActions(contentDoc) {
    const contentDocCopy = JSON.parse(JSON.stringify(contentDoc));
    for (const component of Object.values(contentDocCopy.componentsById)) {
        const actionByTrigger = component.actions;
        if (actionByTrigger) {
            for (const trigger in actionByTrigger) {
                const trg = trigger;
                const actions = actionByTrigger[trg];
                if (typeof actions === 'undefined')
                    continue;
                actionByTrigger[trg] = actions.filter((action) => {
                    if (action.type === IActionCategory.animateModel || action.type === IActionCategory.playVideo) {
                        return action.targetIds.length > 0;
                    }
                    if (action.type === IActionCategory.playSound) {
                        return typeof action.filestoreId === 'string' && action.filestoreId.length > 0;
                    }
                    return true;
                });
            }
            component.actions = actionByTrigger;
        }
    }
    return contentDocCopy;
}
